import { useEffect, useState } from 'react'
import { auditService } from '../services/audit'
import type { AuditLogItem, ChainVerifyResponse } from '../services/audit'
import { Shield, Search, RefreshCw, CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react'
import { authService } from '../../identity/services/auth'
import { adminService, type Tenant } from '../../../modules/identity/services/admin'
import { UserRole } from '../../../types'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Helpers ───────────────────────────────────────────────────────────────────

// ── Operational actions (DP / IH / TL view) ──────────────────────────────────
// Keep in sync with backend AuditLogService calls.
const LEAD_ACTIONS = [
  // Lead lifecycle
  'LEAD_CREATED', 'LEAD_IMPORTED', 'LEAD_UPDATED_VIA_DEDUP',
  'LEAD_STATUS_CHANGED', 'LEAD_REASSIGNED',
  // Call activity
  'CALL_INITIATED', 'CALL_DISPOSITION_UPDATED',
  // Discount workflow
  'DISCOUNT_APPROVED', 'DISCOUNT_REJECTED', 'DISCOUNT_ESCALATED',
  // WhatsApp automation
  'AUTOMATION_MESSAGE_SENT', 'AUTOMATION_MESSAGE_FAILED',
  'AUTOMATION_RULE_CREATED', 'AUTOMATION_RULE_UPDATED', 'AUTOMATION_RULE_DELETED',
  'AUTOMATION_RUN_TRIGGERED',
  // Outcomes
  'POLICY_ISSUED',
  // Privacy & access
  'MOBILE_REVEALED', 'WHATSAPP_OPT_OUT',
  'DPDP_CONSENT_RECORDED', 'DPDP_CONSENT_WITHDRAWN', 'DPDP_DATA_ERASED',
]

// ── Platform actions (BA view) ────────────────────────────────────────────────
// Keep in sync with backend PlatformAuditService calls.
const PLATFORM_ACTIONS = [
  // Tenant & user governance
  'TENANT_CREATED', 'TENANT_UPDATED',
  'USER_CREATED', 'USER_ROLE_CHANGED', 'USER_ACTIVATED', 'USER_DEACTIVATED',
  // Feature management
  'FEATURE_FLAGS_UPDATED',
  // Billing
  'WALLET_CREDITED',
  'SUBSCRIPTION_CREATED', 'TIER_ASSIGNED',
  'SUBSCRIPTION_SUSPENDED', 'SUBSCRIPTION_REACTIVATED', 'SUBSCRIPTION_CANCELLED',
  // WhatsApp templates
  'TEMPLATE_CREATED', 'TEMPLATE_UPDATED', 'TEMPLATE_TOGGLED',
]

const ACTION_LABEL: Record<string, string> = {
  // Tenant
  TENANT_CREATED:           'Tenant Created',
  TENANT_UPDATED:           'Tenant Updated',
  // Users
  USER_CREATED:             'User Created',
  USER_ROLE_CHANGED:        'Role Changed',
  USER_ACTIVATED:           'User Activated',
  USER_DEACTIVATED:         'User Deactivated',
  // Features
  FEATURE_FLAGS_UPDATED:    'Feature Flags Updated',
  // Billing
  WALLET_CREDITED:          'Wallet Credited',
  SUBSCRIPTION_CREATED:     'Subscription Created',
  TIER_ASSIGNED:            'Plan Tier Assigned',
  SUBSCRIPTION_SUSPENDED:   'Subscription Suspended',
  SUBSCRIPTION_REACTIVATED: 'Subscription Reactivated',
  SUBSCRIPTION_CANCELLED:   'Subscription Cancelled',
  // Templates
  TEMPLATE_CREATED:         'Template Registered',
  TEMPLATE_UPDATED:         'Template Updated',
  TEMPLATE_TOGGLED:         'Template Toggled',
  // Discounts
  DISCOUNT_APPROVED:           'Discount Approved',
  DISCOUNT_REJECTED:           'Discount Rejected',
  DISCOUNT_ESCALATED:          'Discount Escalated',
  // Automation
  AUTOMATION_MESSAGE_SENT:     'Automation: Message Sent',
  AUTOMATION_MESSAGE_FAILED:   'Automation: Message Failed',
  AUTOMATION_RULE_CREATED:     'Automation: Rule Created',
  AUTOMATION_RULE_UPDATED:     'Automation: Rule Updated',
  AUTOMATION_RULE_DELETED:     'Automation: Rule Deleted',
  AUTOMATION_RUN_TRIGGERED:    'Automation: Manual Run',
}

const ACTION_BADGE: Record<string, string> = {
  // Tenant
  TENANT_CREATED:           'badge-success',
  TENANT_UPDATED:           'badge-info',
  // Users
  USER_CREATED:             'badge-success',
  USER_ROLE_CHANGED:        'badge-warning',
  USER_ACTIVATED:           'badge-success',
  USER_DEACTIVATED:         'badge-danger',
  // Features
  FEATURE_FLAGS_UPDATED:    'badge-warning',
  // Billing
  WALLET_CREDITED:          'badge-info',
  SUBSCRIPTION_CREATED:     'badge-info',
  TIER_ASSIGNED:            'badge-info',
  SUBSCRIPTION_SUSPENDED:   'badge-warning',
  SUBSCRIPTION_REACTIVATED: 'badge-success',
  SUBSCRIPTION_CANCELLED:   'badge-danger',
  // Templates
  TEMPLATE_CREATED:         'badge-info',
  TEMPLATE_UPDATED:         'badge-neutral',
  TEMPLATE_TOGGLED:         'badge-neutral',
  // Discounts
  DISCOUNT_APPROVED:           'badge-success',
  DISCOUNT_REJECTED:           'badge-danger',
  DISCOUNT_ESCALATED:          'badge-warning',
  // Automation
  AUTOMATION_MESSAGE_SENT:     'badge-success',
  AUTOMATION_MESSAGE_FAILED:   'badge-danger',
  AUTOMATION_RULE_CREATED:     'badge-info',
  AUTOMATION_RULE_UPDATED:     'badge-neutral',
  AUTOMATION_RULE_DELETED:     'badge-danger',
  AUTOMATION_RUN_TRIGGERED:    'badge-info',
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

// Human-friendly labels for change keys
const CHANGE_LABELS: Record<string, string> = {
  // Billing
  amount:           'Amount',
  balance_after:    'Balance after',
  description:      'Note',
  service:          'Service',
  tier:             'Plan',
  cycle:            'Cycle',
  price:            'Price',
  // Tenant
  domain:           'Domain',
  dp_email:         'DP email',
  dp_name:          'DP name',
  initial_credit:   'Initial credit',
  // User management
  role:             'Role',
  full_name:        'Name',
  old_role:         'Previous role',
  new_role:         'New role',
  created_by_role:  'Created by',
  was_active:       'Was active',
  now_active:       'Now active',
  // Features
  flags:            'Flags changed',
  // Templates
  name:             'Template name',
  category:         'Category',
  language:         'Language',
  active:           'Active',
  // Discount
  od_discount:      'OD discount offered',
  approved_by_role: 'Approved by',
  rejected_by_role: 'Rejected by',
  escalated_to:     'Escalated to',
  comment:          'Comment',
  reason:           'Reason',
  // Automation
  rule:             'Rule',
  template:         'Template',
  trigger:          'Trigger status',
  source:           'Source',
  error:            'Error',
  triggered_by:     'Triggered by',
}

const CURRENCY_KEYS = new Set(['amount', 'balance_after', 'price', 'initial_credit'])

function formatValue(key: string, val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (CURRENCY_KEYS.has(key) && typeof val === 'number') {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val)
  }
  return String(val)
}

function InlineChanges({ changes }: { changes: Record<string, unknown> | null }) {
  if (!changes || Object.keys(changes).length === 0) {
    return <span style={{ color: 'var(--text-tertiary)' }}>—</span>
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-0.5">
      {Object.entries(changes).map(([k, v]) => (
        <span key={k} className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>{CHANGE_LABELS[k] ?? k}:</span>{' '}
          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatValue(k, v)}</span>
        </span>
      ))}
    </div>
  )
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function ChainStatusBanner({ result }: { result: ChainVerifyResponse }) {
  return (
    <div className={result.chain_intact ? 'alert-success' : 'alert-danger'}>
      {result.chain_intact
        ? <CheckCircle className="h-5 w-5 shrink-0" />
        : <XCircle className="h-5 w-5 shrink-0" />
      }
      <div>
        <p className="font-medium text-sm">
          {result.chain_intact
            ? 'Audit chain intact'
            : `Chain integrity failure — ${result.failure_count} issue${result.failure_count !== 1 ? 's' : ''} detected`}
        </p>
        <p className="text-xs mt-0.5 opacity-80">
          {result.total_records} records verified · {new Date(result.verified_at).toLocaleString()}
        </p>
        {!result.chain_intact && result.first_broken_at_log_id && (
          <p className="text-xs mt-1">First break at log #{result.first_broken_at_log_id}</p>
        )}
      </div>
    </div>
  )
}

function ChangesCell({ changes }: { changes: Record<string, unknown> | null }) {
  const [expanded, setExpanded] = useState(false)
  if (!changes || Object.keys(changes).length === 0) {
    return <span style={{ color: 'var(--text-tertiary)' }}>—</span>
  }
  const keys = Object.keys(changes)
  const preview = keys.slice(0, 2).join(', ') + (keys.length > 2 ? ` +${keys.length - 2}` : '')
  return (
    <div>
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-1 text-xs hover:underline"
        style={{ color: 'var(--brand-primary)' }}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        {preview}
      </button>
      {expanded && (
        <pre
          className="mt-1 text-xs p-2 rounded-md max-w-xs overflow-x-auto whitespace-pre-wrap"
          style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}
        >
          {JSON.stringify(changes, null, 2)}
        </pre>
      )}
    </div>
  )
}

// ── Platform audit view (BACKEND_ADMIN) ───────────────────────────────────────

function PlatformAuditView() {
  const [items, setItems]       = useState<AuditLogItem[]>([])
  const [tenantMap, setTenantMap] = useState<Record<number, string>>({})
  const [loading, setLoading]   = useState(false)
  const [page, setPage]         = useState(1)
  const [hasMore, setHasMore]   = useState(false)
  const [filters, setFilters]   = useState({ action: '', entity_type: '' })
  const isMobile = useWindowWidth() < 768
  const [filtersOpen, setFiltersOpen] = useState(false)

  const PAGE_SIZE = 50

  const load = async (p = 1) => {
    setLoading(true)
    try {
      const [res, tenants] = await Promise.all([
        auditService.getPlatformLogs({ page: p, page_size: PAGE_SIZE,
          ...(filters.action      ? { action: filters.action }           : {}),
          ...(filters.entity_type ? { entity_type: filters.entity_type } : {}),
        }),
        p === 1 ? adminService.listTenants() : Promise.resolve(null as Tenant[] | null),
      ])
      setItems(p === 1 ? res.items : prev => [...prev, ...res.items])
      setHasMore(res.items.length === PAGE_SIZE)
      setPage(p)
      if (tenants) {
        setTenantMap(Object.fromEntries(tenants.map(t => [t.id, t.name])))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(1) }, [])

  return (
    <div className="space-y-4">
      {/* Filters */}
      <form onSubmit={e => { e.preventDefault(); load(1) }} className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Mobile: collapsible header */}
        {isMobile ? (
          <>
            <div
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
              onClick={() => setFiltersOpen(o => !o)}
            >
              <Search className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary)' }}>Filters</span>
              {(filters.action || filters.entity_type) && (
                <span className="badge-info" style={{ fontSize: 10 }}>
                  {[filters.action, filters.entity_type].filter(Boolean).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 transition-transform" style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }} />
            </div>
            {filtersOpen && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Action</label>
                    <select className="input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
                      <option value="">All</option>
                      {PLATFORM_ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABEL[a] ?? a}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Entity</label>
                    <select className="input" value={filters.entity_type} onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))}>
                      <option value="">All</option>
                      <option value="tenant">Tenant</option>
                      <option value="wallet">Wallet</option>
                      <option value="subscription">Subscription</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="submit" disabled={loading} className="btn-primary flex-1">Search</button>
                  <button type="button" className="btn-secondary" onClick={() => { setFilters({ action: '', entity_type: '' }); load(1) }}>Clear</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '14px' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="form-group">
                <label className="form-label">Action</label>
                <select className="input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
                  <option value="">All actions</option>
                  {PLATFORM_ACTIONS.map(a => <option key={a} value={a}>{ACTION_LABEL[a] ?? a}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Entity type</label>
                <select className="input" value={filters.entity_type} onChange={e => setFilters(f => ({ ...f, entity_type: e.target.value }))}>
                  <option value="">All</option>
                  <option value="tenant">Tenant</option>
                  <option value="wallet">Wallet</option>
                  <option value="subscription">Subscription</option>
                </select>
              </div>
              <div className="form-group flex items-end gap-2">
                <button type="submit" disabled={loading} className="btn-primary"><Search className="h-3.5 w-3.5" /> Search</button>
                <button type="button" onClick={() => { setFilters({ action: '', entity_type: '' }); load(1) }} className="btn-secondary">Clear</button>
                <button type="button" onClick={() => load(1)} disabled={loading} className="btn-icon ml-auto" title="Refresh">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Table / Cards */}
      {isMobile ? (
        loading && items.length === 0 ? (
          <div className="py-10 flex justify-center"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No platform activity yet</p></div>
        ) : (
          <div className="flex flex-col gap-2">
            {(items as (AuditLogItem & { tenant_name?: string; tenant_id?: number })[]).map(item => {
              const resolvedName = (item.tenant_id ? tenantMap[item.tenant_id] : null) ?? item.tenant_name ?? null
              return (
                <div key={item.id} className="card" style={{ padding: '10px 12px' }}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ACTION_BADGE[item.action] ?? 'badge-neutral'}`}>
                      {ACTION_LABEL[item.action] ?? item.action}
                    </span>
                    <span className="text-xs shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                      {item.timestamp ? fmtDateTime(item.timestamp) : '—'}
                    </span>
                  </div>
                  {resolvedName && (
                    <p className="text-xs mt-1 font-medium" style={{ color: 'var(--text-primary)' }}>{resolvedName}</p>
                  )}
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{item.actor}</p>
                  {item.changes && Object.keys(item.changes).length > 0 && (
                    <div className="mt-1"><InlineChanges changes={item.changes} /></div>
                  )}
                </div>
              )
            })}
            {hasMore && (
              <button onClick={() => load(page + 1)} disabled={loading} className="btn-secondary w-full">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="hidden sm:table-cell">When</th>
                  <th>Action</th>
                  <th className="hidden md:table-cell">Tenant</th>
                  <th>Details</th>
                  <th className="hidden sm:table-cell">By</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center" style={{ color: 'var(--text-tertiary)' }}>
                      No platform activity yet. Actions like onboarding tenants and assigning plans will appear here.
                    </td>
                  </tr>
                ) : (
                  items.map((item: AuditLogItem & { tenant_name?: string; tenant_id?: number }) => {
                    const resolvedName =
                      (item.tenant_id ? tenantMap[item.tenant_id] : null)
                      ?? item.tenant_name
                      ?? null
                    return (
                      <tr key={item.id}>
                        <td className="hidden sm:table-cell" style={{ color: 'var(--text-tertiary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {item.timestamp ? fmtDateTime(item.timestamp) : '—'}
                        </td>
                        <td>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ACTION_BADGE[item.action] ?? 'badge-neutral'}`}>
                            {ACTION_LABEL[item.action] ?? item.action}
                          </span>
                        </td>
                        <td className="hidden md:table-cell">
                          {resolvedName
                            ? <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{resolvedName}</span>
                            : <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                          }
                        </td>
                        <td><InlineChanges changes={item.changes} /></td>
                        <td className="hidden sm:table-cell" style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{item.actor}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="px-4 py-3" style={{ borderTop: '1px solid var(--border-default)' }}>
              <button onClick={() => load(page + 1)} disabled={loading} className="btn-secondary w-full">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Operational audit view (DP / IH / TL) ────────────────────────────────────

function OperationalAuditView() {
  const [items, setItems]     = useState<AuditLogItem[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage]       = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [chainResult, setChainResult] = useState<ChainVerifyResponse | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [filters, setFilters] = useState({ lead_id: '', action: '', from_date: '', to_date: '' })
  const isMobile = useWindowWidth() < 768
  const [filtersOpen, setFiltersOpen] = useState(false)

  const PAGE_SIZE = 50

  const load = async (p = 1) => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { page: p, page_size: PAGE_SIZE }
      if (filters.lead_id)   params.lead_id   = Number(filters.lead_id)
      if (filters.action)    params.action    = filters.action
      if (filters.from_date) params.from_date = filters.from_date
      if (filters.to_date)   params.to_date   = filters.to_date
      const res = await auditService.getLogs(params)
      setItems(p === 1 ? res.items : prev => [...prev, ...res.items])
      setHasMore(res.items.length === PAGE_SIZE)
      setPage(p)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(1) }, [])

  const handleVerifyChain = async () => {
    setVerifying(true)
    try { setChainResult(await auditService.verifyChain()) }
    finally { setVerifying(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={handleVerifyChain} disabled={verifying} className="btn-secondary">
          <CheckCircle className={`h-3.5 w-3.5 ${verifying ? 'animate-pulse' : ''}`} />
          {verifying ? 'Verifying…' : 'Verify Chain Integrity'}
        </button>
      </div>

      {chainResult && <ChainStatusBanner result={chainResult} />}

      <form onSubmit={e => { e.preventDefault(); load(1) }} className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isMobile ? (
          <>
            <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer" onClick={() => setFiltersOpen(o => !o)}>
              <Search className="h-3.5 w-3.5" style={{ color: 'var(--text-tertiary)' }} />
              <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary)' }}>Filters</span>
              {[filters.lead_id, filters.action, filters.from_date, filters.to_date].filter(Boolean).length > 0 && (
                <span className="badge-info" style={{ fontSize: 10 }}>
                  {[filters.lead_id, filters.action, filters.from_date, filters.to_date].filter(Boolean).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 transition-transform" style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }} />
            </div>
            {filtersOpen && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                <div className="grid grid-cols-2 gap-2">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Lead ID</label>
                    <input type="number" className="input" placeholder="Any" value={filters.lead_id}
                      onChange={e => setFilters(f => ({ ...f, lead_id: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Action</label>
                    <select className="input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
                      <option value="">All</option>
                      {LEAD_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">From</label>
                    <input type="datetime-local" className="input" value={filters.from_date}
                      onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">To</label>
                    <input type="datetime-local" className="input" value={filters.to_date}
                      onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="submit" disabled={loading} className="btn-primary flex-1">Search</button>
                  <button type="button" className="btn-secondary"
                    onClick={() => { setFilters({ lead_id: '', action: '', from_date: '', to_date: '' }); load(1) }}>Clear</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '14px' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="form-group">
                <label className="form-label">Lead ID</label>
                <input type="number" className="input" placeholder="Any" value={filters.lead_id}
                  onChange={e => setFilters(f => ({ ...f, lead_id: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Action</label>
                <select className="input" value={filters.action} onChange={e => setFilters(f => ({ ...f, action: e.target.value }))}>
                  <option value="">All actions</option>
                  {LEAD_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">From</label>
                <input type="datetime-local" className="input" value={filters.from_date}
                  onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">To</label>
                <input type="datetime-local" className="input" value={filters.to_date}
                  onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button type="submit" disabled={loading} className="btn-primary"><Search className="h-3.5 w-3.5" /> Search</button>
              <button type="button" className="btn-secondary"
                onClick={() => { setFilters({ lead_id: '', action: '', from_date: '', to_date: '' }); load(1) }}>Clear</button>
              <button type="button" onClick={() => load(1)} disabled={loading} className="btn-icon ml-auto" title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        )}
      </form>

      {isMobile ? (
        loading && items.length === 0 ? (
          <div className="py-10 flex justify-center"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No audit logs match your filters</p></div>
        ) : (
          <div className="flex flex-col gap-2">
            {items.map(item => (
              <div key={item.id} className="card" style={{ padding: '10px 12px' }}>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded"
                    style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}>
                    {item.action}
                  </span>
                  <span className="text-xs shrink-0" style={{ color: 'var(--text-tertiary)' }}>
                    {item.timestamp ? new Date(item.timestamp).toLocaleString() : '—'}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  {item.lead_id && (
                    <span className="text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>Lead #{item.lead_id}</span>
                  )}
                  {item.actor && (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{item.actor}</span>
                  )}
                </div>
                {item.changes && Object.keys(item.changes).length > 0 && (
                  <div className="mt-1"><ChangesCell changes={item.changes} /></div>
                )}
              </div>
            ))}
            {hasMore && (
              <button onClick={() => load(page + 1)} disabled={loading} className="btn-secondary w-full">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="hidden sm:table-cell">ID</th><th>Action</th><th className="hidden sm:table-cell">Lead</th><th className="hidden md:table-cell">Actor</th><th>Changes</th><th className="hidden sm:table-cell">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 ? (
                  <tr><td colSpan={6} className="py-12 text-center"><div className="spinner mx-auto" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={6} className="py-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                    No audit logs match your filters
                  </td></tr>
                ) : (
                  items.map(item => (
                    <tr key={item.id}>
                      <td className="font-mono text-xs hidden sm:table-cell" style={{ color: 'var(--text-tertiary)' }}>{item.id}</td>
                      <td>
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)' }}>
                          {item.action}
                        </span>
                      </td>
                      <td className="hidden sm:table-cell">{item.lead_id ? `#${item.lead_id}` : '—'}</td>
                      <td className="hidden md:table-cell">{item.actor}</td>
                      <td><ChangesCell changes={item.changes} /></td>
                      <td className="text-xs whitespace-nowrap hidden sm:table-cell" style={{ color: 'var(--text-tertiary)' }}>
                        {item.timestamp ? new Date(item.timestamp).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="px-4 py-3" style={{ borderTop: '1px solid var(--border-default)' }}>
              <button onClick={() => load(page + 1)} disabled={loading} className="btn-secondary w-full">
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Page shell ────────────────────────────────────────────────────────────────

export default function AuditLogPage() {
  const currentUser = authService.getCurrentUser()
  const isAdmin = currentUser?.role === UserRole.BACKEND_ADMIN

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            Audit Logs
          </h1>
          <p className="page-subtitle">
            {isAdmin
              ? 'Platform governance — tenant, billing, and wallet activity'
              : 'Tamper-evident operational trail for leads and policies'
            }
          </p>
        </div>
      </div>

      {isAdmin ? <PlatformAuditView /> : <OperationalAuditView />}
    </div>
  )
}
